// EventTracker.js
import { analytics, logEvent } from './firebase';

/**
 * Safely logs an event to Firebase Analytics
 * @param {string} eventName - The name of the event
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
    try {
        logEvent(analytics, eventName, { ...params, created_at: new Date().toISOString() });
    } catch (error) {
        // console.error(`[Firebase] Failed to log event: ${eventName}`, error);
    }
};

/**
 * Logs a screen view
 * @param {string} screenName - Name of the screen
 * @param {string} screenClass - Component or screen class
 */
export const trackScreenView = (screenName, screenClass, platform, version) => {
    trackEvent('screen_view', {
        firebase_screen: screenName,
        firebase_screen_class: screenClass,
        platform: platform,
        version: process.env.REACT_APP_VERSION
    });
};

/**
 * Logs user engagement event
 * @param {string} userId - ID of the user
 * @param {number} durationMs - Engagement time in milliseconds
 */
export const trackUserEngagement = (userId, durationMs = 10000, platform, version) => {
    trackEvent('user_engagement', {
        user_id: userId,
        engagement_time_msec: durationMs,
        platform: platform,
        version: process.env.REACT_APP_VERSION
    });
};

/**
 * Logs invoice creation event
 * @param {object} invoice - Invoice object
 */
export const trackInvoiceCreated = (id, customer, amount, platform, version) => {
    trackEvent('invoice_create', {
        invoice_id: id,
        amount: amount,
        created_at: new Date().toISOString(),
        customer: customer || 'unknown',
        platform: platform,
        version: process.env.REACT_APP_VERSION
    });
};

/**
 * Logs WhatsApp share event
 * @param {string} invoiceId - ID of the invoice
 * @param {string} toNumber - Customer phone number
 * @param {string} userId - ID of the user who shared
 */
export const trackWhatsAppShare = (invoiceId, toNumber, userId, platform, version) => {
    trackEvent('whatsapp_share', {
        invoice_id: invoiceId,
        to_number: toNumber,
        user_id: userId,
        platform: platform,
        version: process.env.REACT_APP_VERSION
    });
};


// ─────────────────────────────────────────────
// Subscription Flow Trackers
// ─────────────────────────────────────────────

/**
 * Fired when the Pricing / Subscription page is viewed.
 * @param {string} userId   - Firebase UID (or null for guests)
 * @param {string} currency - Active tab: "INR" | "USD"
 */
export const trackSubscriptionViewed = (userId, currency = 'INR') => {
    trackEvent('subscription_page_viewed', {
        user_id: userId || 'guest',
        currency,
        platform: 'web',
        version: process.env.REACT_APP_VERSION,
    });
};

/**
 * Fired when a user clicks "Get Now" / "Get Started" (GA4 begin_checkout).
 * @param {string}       planName - e.g. "Starter Plan"
 * @param {string}       planId   - Razorpay plan ID
 * @param {string}       currency - "INR" | "USD"
 * @param {string|number} price   - Plan price (e.g. 499)
 * @param {string}       userId   - Firebase UID
 */
export const trackPlanSelected = (planName, planId, currency, price, userId) => {
    trackEvent('begin_checkout', {
        plan_name: planName,
        plan_id: planId,
        currency,
        value: price,
        user_id: userId || 'guest',
        platform: 'web',
        version: process.env.REACT_APP_VERSION,
    });
};

/**
 * Fired after CreateSubscription API returns a valid subscriptionId
 * (Razorpay checkout is about to open).
 * @param {string} planName       - e.g. "Professional Plan"
 * @param {string} subscriptionId - Razorpay subscription ID
 * @param {string} userId         - Firebase UID
 */
export const trackSubscriptionInitiated = (planName, subscriptionId, userId) => {
    trackEvent('subscription_initiated', {
        plan_name: planName,
        subscription_id: subscriptionId,
        user_id: userId || 'guest',
        platform: 'web',
        version: process.env.REACT_APP_VERSION,
    });
};

/**
 * Fired after payment is verified and subscription confirmed (GA4 purchase).
 * @param {string}       planName       - Resolved plan name
 * @param {string}       subscriptionId - Razorpay subscription ID
 * @param {string}       paymentId      - Razorpay payment ID
 * @param {string}       currency       - "INR" | "USD"
 * @param {string|number} value         - Amount paid
 * @param {string}       userId         - Firebase UID
 */
export const trackPaymentSuccess = (planName, subscriptionId, paymentId, currency, value, userId) => {
    trackEvent('purchase', {
        plan_name: planName,
        subscription_id: subscriptionId,
        payment_id: paymentId,
        currency: currency || 'INR',
        value: value || 0,
        user_id: userId || 'guest',
        platform: 'web',
        version: process.env.REACT_APP_VERSION,
    });
};

/**
 * Fired when Razorpay raises a payment.failed event.
 * @param {string} subscriptionId - Razorpay subscription ID
 * @param {string} reason         - Failure reason / error description
 * @param {string} userId         - Firebase UID
 */
export const trackPaymentFailed = (subscriptionId, reason, userId) => {
    trackEvent('payment_failed', {
        subscription_id: subscriptionId,
        reason: reason || 'unknown',
        user_id: userId || 'guest',
        platform: 'web',
        version: process.env.REACT_APP_VERSION,
    });
};

/**
 * Fired when the user closes the Razorpay modal without paying.
 * @param {string} subscriptionId - Razorpay subscription ID
 * @param {string} userId         - Firebase UID
 */
export const trackPaymentDismissed = (subscriptionId, userId) => {
    trackEvent('payment_dismissed', {
        subscription_id: subscriptionId,
        user_id: userId || 'guest',
        platform: 'web',
        version: process.env.REACT_APP_VERSION,
    });
};
